/**
 * Modern presentational primitives scoped to the Organization page.
 *
 * These intentionally live here (not in the shared component library) so the
 * "control console" restyle of the org tabs can move ahead on the cyan/slate
 * brand direction without touching components other pages still depend on.
 */
import { cn } from "helpers/classNames";
import { ComponentType, ReactNode, SVGProps } from "react";

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export type Accent = "cyan" | "green" | "amber" | "violet" | "rose" | "slate";

const accentTile: Record<Accent, string> = {
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-100",
  green: "bg-green-50 text-green-700 ring-green-100",
  amber: "bg-amber-50 text-amber-700 ring-amber-100",
  violet: "bg-indigo-50 text-indigo-700 ring-indigo-100",
  rose: "bg-rose-50 text-rose-700 ring-rose-100",
  slate: "bg-slate-100 text-slate-600 ring-slate-200",
};

/* -------------------------------------------------------------------------- */
/* Card                                                                       */
/* -------------------------------------------------------------------------- */

export const Card = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm",
      className,
    )}
  >
    {children}
  </div>
);

type CardHeaderProps = {
  icon: IconType;
  title: string;
  description?: string;
  accent?: Accent;
  action?: ReactNode;
};

export const CardHeader = ({
  icon: Icon,
  title,
  description,
  accent = "cyan",
  action,
}: CardHeaderProps) => (
  <div className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
    <div className="flex items-start gap-3.5">
      <span
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ring-inset",
          accentTile[accent],
        )}
      >
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <div>
        <h3 className="text-base font-semibold tracking-tight text-slate-900">
          {title}
        </h3>
        {description && (
          <p className="mt-0.5 max-w-prose text-sm text-slate-500">
            {description}
          </p>
        )}
      </div>
    </div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
);

export const CardBody = ({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) => (
  <div className={cn("px-5 py-5 sm:px-6", className)}>{children}</div>
);

/* -------------------------------------------------------------------------- */
/* Detail list (label / value rows)                                           */
/* -------------------------------------------------------------------------- */

export const DetailList = ({ children }: { children: ReactNode }) => (
  <dl className="divide-y divide-slate-100">{children}</dl>
);

export const DetailRow = ({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) => (
  <div className="grid grid-cols-1 gap-1 py-3.5 sm:grid-cols-[minmax(0,15rem)_1fr] sm:gap-6 sm:py-4">
    <dt className="text-sm font-medium text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-900">{children || <Empty />}</dd>
  </div>
);

const Empty = () => <span className="text-slate-300">—</span>;

/* -------------------------------------------------------------------------- */
/* Status badge                                                               */
/* -------------------------------------------------------------------------- */

const badgeColors: Record<Accent, string> = {
  cyan: "bg-cyan-50 text-cyan-700 ring-cyan-600/15",
  green: "bg-green-50 text-green-700 ring-green-600/15",
  amber: "bg-amber-50 text-amber-800 ring-amber-600/20",
  violet: "bg-indigo-50 text-indigo-700 ring-indigo-600/15",
  rose: "bg-rose-50 text-rose-700 ring-rose-600/15",
  slate: "bg-slate-100 text-slate-600 ring-slate-500/15",
};

const dotColors: Record<Accent, string> = {
  cyan: "bg-cyan-500",
  green: "bg-green-500",
  amber: "bg-amber-500",
  violet: "bg-indigo-500",
  rose: "bg-rose-500",
  slate: "bg-slate-400",
};

type StatusBadgeProps = {
  color?: Accent;
  dot?: boolean;
  icon?: IconType;
  children: ReactNode;
  className?: string;
};

export const StatusBadge = ({
  color = "slate",
  dot = false,
  icon: Icon,
  children,
  className,
}: StatusBadgeProps) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
      badgeColors[color],
      className,
    )}
  >
    {dot && (
      <span className={cn("h-1.5 w-1.5 rounded-full", dotColors[color])} />
    )}
    {Icon && <Icon className="h-3.5 w-3.5" aria-hidden="true" />}
    {children}
  </span>
);

/* -------------------------------------------------------------------------- */
/* Empty state                                                                */
/* -------------------------------------------------------------------------- */

export const EmptyState = ({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: IconType;
  title: string;
  description?: string;
  action?: ReactNode;
}) => (
  <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-6 py-12 text-center">
    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-400 ring-1 ring-slate-200">
      <Icon className="h-5 w-5" aria-hidden="true" />
    </span>
    <p className="mt-3 text-sm font-medium text-slate-700">{title}</p>
    {description && (
      <p className="mt-1 max-w-sm text-sm text-slate-500">{description}</p>
    )}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/* -------------------------------------------------------------------------- */
/* Toggle (cyan, brand-aligned)                                               */
/* -------------------------------------------------------------------------- */

export const Toggle = ({
  isEnabled,
  onChange,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
}: {
  isEnabled: boolean;
  onChange: (val: boolean) => void;
  "aria-label"?: string;
  "aria-labelledby"?: string;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={isEnabled}
    aria-label={ariaLabel}
    aria-labelledby={ariaLabelledby}
    onClick={() => onChange(!isEnabled)}
    className={cn(
      "relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 focus-visible:ring-offset-2",
      isEnabled ? "bg-cyan-600" : "bg-slate-200",
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
        isEnabled ? "translate-x-5" : "translate-x-0",
      )}
    />
  </button>
);

/* -------------------------------------------------------------------------- */
/* Setting row (toggle + label + description)                                 */
/* -------------------------------------------------------------------------- */

export const SettingRow = ({
  title,
  description,
  control,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  control: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-white p-4",
      className,
    )}
  >
    <div className="min-w-0">
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      {description && (
        <p className="mt-1 text-sm leading-relaxed text-slate-500">
          {description}
        </p>
      )}
    </div>
    <div className="shrink-0 pt-0.5">{control}</div>
  </div>
);

/* -------------------------------------------------------------------------- */
/* Micro label                                                                */
/* -------------------------------------------------------------------------- */

export const MicroLabel = ({ children }: { children: ReactNode }) => (
  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
    {children}
  </p>
);
